import { Stack, useToast } from "@chakra-ui/react";
import {
  AssetInput,
  PolicyAssetNode,
  useCreateAssetMutation,
  useCreatePolicyDocumentMutation,
  useCreatePolicyMutation,
  useCreatePolicyNoteMutation,
} from "graphql/queries/generated/queries";
import { NewPolicyForm, NewAssetDrawer, ImportAssetsDrawer } from "pages/spin/shared";
import { useForm, FormProvider, useFieldArray, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { PolicyInputSchema } from "graphql/queries/overrides";
import { useEffect, useState } from "react";
import { SelectedAsset } from "components/ui/PolicyAssetSearchBox/types";
import placeholderPNG from "assets/placeholder.png";
import { PolicyInputExtended } from "pages/spin/types";
import { ValidationProvider } from "components/form/ValidationContext";
import defaultValues from "pages/spin/shared/defaultValues";
import { BaseRow } from "react-csv-importer";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useGetOrCreateRegistry from "hooks/useGetOrCreateRegistry";
import useGetAssetCurrentValue from "hooks/useGetAssetCurrentValue";

const policyAssetFields = {
  asset: "",
  catNat: false,
  coverTerrorism: false,
  coveredRiskType: "",
  coverType: "",
  evaluationType: "",
  exemption: false,
  title: "",
  author: "",
  creationPeriod: "",
  lenderId: "",
  lenderAddress: {
    raw: "",
  },
  pickupAddress: {
    raw: "",
  },
  deliveryAddress: {
    raw: "",
  },
  shipperId: "",
  insuredValue: {
    amount: null as number | null,
    currency: "EUR",
  },
};

const TEPoliciesNewPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const [createAsset, { data: createAssetData, error: createAssetError }] =
    useCreateAssetMutation();
  const [showNewAssetDrawer, setShowNewAssetDrawer] = useState<boolean>(false);
  const [showImportAssetsDrawer, setShowImportAssetsDrawer] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState(0);
  const getOrCreateRegistry = useGetOrCreateRegistry();
  const getAssetCurrentValue = useGetAssetCurrentValue();

  const methods = useForm<PolicyInputExtended>({
    defaultValues: { ...defaultValues, policyType: "TEMPORARY_EXHIBITION" },
    resolver: yupResolver(PolicyInputSchema()),
  });

  const [createPolicy, { data: createPolicyData, error: createPolicyError }] =
    useCreatePolicyMutation();
  const [
    createPolicyDocument,
    { data: createPolicyDocumentData, error: createPolicyDocumentError },
  ] = useCreatePolicyDocumentMutation();
  const [createPolicyNote, { data: createPolicyNoteData, error: createPolicyNoteError }] =
    useCreatePolicyNoteMutation();

  useEffect(() => {
    if (createPolicyData) {
      toast({
        title: t("Policy has been created."),
        description: t(""),
        status: "success",
        duration: 9000,
        isClosable: true,
      });

      const {
        createPolicy: { policy },
      } = createPolicyData;
      setTimeout(() => {
        navigate(`/spin/te-policies/${policy.id}`);
      }, Number(process.env.REACT_APP_REDIRECT_TIMEOUT) ?? 3000);
    }

    if (createPolicyError)
      toast({
        title: t("Policy couldn't be created."),
        description: t(`Error: ${createPolicyError.message}`),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
  }, [createPolicyData, createPolicyError]);

  const {
    formState: { errors },
    setFocus,
    setValue,
  } = methods;

  useEffect(() => {
    const firstError = Object.keys(errors).reduce(
      (field, a) => ((errors as unknown as never)[field] ? field : a),
      null
    );

    try {
      if (firstError) {
        setTabIndex(0);
        setFocus(firstError as keyof PolicyInputExtended);
      }
    } catch (e) {
      console.log(e);
    }
  }, [errors, setFocus]);

  const { fields, append, remove, update } = useFieldArray({
    control: methods.control,
    name: "policyAssets",
  });

  const {
    fields: noteFields,
    append: appendNote,
    remove: removeNote,
  } = useFieldArray({
    control: methods.control,
    name: "policyNote",
  });
  const {
    fields: documentFields,
    append: appendDocument,
    remove: removeDocument,
  } = useFieldArray({
    control: methods.control,
    name: "policyDocuments",
  });

  const watchPolicyAssets = methods.watch("policyAssets");
  const watchEveryPolicyAssets = watchPolicyAssets?.map((_, index) =>
    methods.watch(`policyAssets.${index}.insuredValue`)
  );

  useEffect(() => {
    if (!watchEveryPolicyAssets || watchEveryPolicyAssets.length === 0) return;
    let totalInsuredValue = 0;
    // eslint-disable-next-line array-callback-return
    const totalValue = watchEveryPolicyAssets.map(({ amount }, index) => {
      if (!Number.isNaN(amount)) {
        totalInsuredValue += amount;
        return amount;
      }
      return 0;
    });
    setValue("totalInsuredValue.amount", totalInsuredValue);
  }, [watchEveryPolicyAssets]);

  const goToNextTab = () => {
    setTabIndex((prevIndex) => (prevIndex < 3 && prevIndex >= 0 ? prevIndex + 1 : prevIndex));
  };

  const goToPreviousTab = () => {
    setTabIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const handleTabChange = (index: number) => {
    setTabIndex(index);
  };

  const addPolicyAsset = async ({
    id,
    title,
    author,
    creationPeriod,
    objectID,
    gqlId,
    image,
  }: Partial<SelectedAsset>) => {
    const {
      evaluationType,
      catNat,
      coverTerrorism,
      coveredRiskType,
      coverType,
      eventEntityId,
      eventLocationEntityId,
    } = methods.getValues();

    const { amount, currency } = (await getAssetCurrentValue(gqlId)) || {};

    append({
      ...policyAssetFields,
      asset: gqlId,
      title,
      image: image ?? placeholderPNG,
      author,
      creationPeriod,
      evaluationType,
      catNat,
      coverTerrorism,
      coveredRiskType,
      coverType,
      insuredValue: {
        amount: amount ?? null,
        currency: currency ?? "EUR",
      },
      objectLocationEntityId: eventEntityId,
      objectLocationOfficeId: eventLocationEntityId,
    });
  };

  const onSubmit: SubmitHandler<PolicyInputExtended> = async (formData, e) => {
    const { policyAssets, policyDocuments, policyNote, ...policyData } = formData;

    const cleanPolicyAssets = policyAssets.map((policyAsset) => {
      const { title, creationPeriod, image, author, ...policyAssetData } = policyAsset;
      return policyAssetData;
    });

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const hasFile = policyDocuments.some(({ file }) => (file as unknown as FileList).length! > 0);

    const { data: createPolicyEvalutedData, errors: createPolicyEvaluatedErrors } =
      await createPolicy({
        variables: {
          input: {
            policyData: {
              ...policyData,
              policyAssets: cleanPolicyAssets,
              policyDocuments: hasFile ? policyDocuments : null,
              policyNote,
            },
          },
        },
      });

    if (createPolicyEvaluatedErrors) return;

    const {
      createPolicy: {
        policy: { id: policyId },
      },
    } = createPolicyEvalutedData;
  };

  const processCsvRows = async (rows: BaseRow[], { startIndex }: { startIndex: number }) => {
    const result = await Promise.all(
      // eslint-disable-next-line consistent-return
      rows.map(async (row: AssetInput & PolicyAssetNode, index: number) => {
        console.log({ row });
        const { lender, shipper, ...copiedRow } = row;
        // if (!row.category) {
        //   copiedRow.category = "---";
        // }

        if (!row.subcategory) {
          copiedRow.subcategory = "";
        }

        if (!row.title.trim()) {
          toast({
            title: t(`Asset at row ${index} couldn't be uploaded`),
            description: t(`Error: Asset is missing title, which is a required field.`),
            status: "error",
            duration: 9000,
            isClosable: true,
          });

          return null;
        }

        const authorEntityId = await getOrCreateRegistry(row.author);

        const lenderId = lender ? await getOrCreateRegistry(row.lender as unknown as string) : null;
        const shipperId = shipper
          ? await getOrCreateRegistry(row.shipper as unknown as string)
          : null;
        const { data } = await createAsset({
          variables: {
            input: {
              assetData: {
                ...copiedRow,
                authorEntityId,
                validated: false,
              },
            },
          },
        });

        if (!data.createAsset) throw new Error("Error importing asset");
        const {
          createAsset: { asset },
        } = data;

        const { id, title, author, creationPeriod } = asset;

        const {
          evaluationType,
          catNat,
          coverTerrorism,
          coveredRiskType,
          coverType,
          eventEntityId,
          eventLocationEntityId,
        } = methods.getValues();

        append({
          ...policyAssetFields,
          id,
          title,
          author,
          creationPeriod,
          evaluationType: row.evaluationType ?? evaluationType,
          catNat: row.catNat ?? catNat,
          coverTerrorism: row.coverTerrorism ?? coverTerrorism,
          coveredRiskType: row.coveredRiskType ?? coveredRiskType,
          coverType: row.coverType ?? coverType,
          lenderId: lenderId ?? "",
          shipperId: shipperId ?? "",
          lenderAddress: {
            raw: (row.lenderAddress as string) ?? "",
          },
          objectLocationEntityId: eventEntityId,
          objectLocationOfficeId: eventLocationEntityId,
        });
      })
    );
  };

  return (
    <Stack spacing="4">
      <FormProvider {...methods}>
        <ValidationProvider schema={PolicyInputSchema()}>
          <NewPolicyForm
            getValues={methods.getValues}
            policyAssets={watchPolicyAssets}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={methods.handleSubmit(onSubmit)}
            goToNextTab={goToNextTab}
            goToPreviousTab={goToPreviousTab}
            handleTabChange={handleTabChange}
            tabIndex={tabIndex}
            data={createPolicyData}
            loading={methods.formState.isSubmitting}
            error={[createPolicyError, createPolicyNoteError, createPolicyDocumentError]}
            extraFields={{ fields, append, remove, update }}
            setShowNewAssetDrawer={setShowNewAssetDrawer}
            setShowImportAssetsDrawer={setShowImportAssetsDrawer}
            addPolicyAsset={addPolicyAsset}
            policyType="TEMPORARY_EXHIBITION"
          />
        </ValidationProvider>
      </FormProvider>
      <NewAssetDrawer
        show={showNewAssetDrawer}
        onClose={() => setShowNewAssetDrawer(false)}
        addPolicyAsset={addPolicyAsset}
      />
      <ImportAssetsDrawer
        show={showImportAssetsDrawer}
        onClose={() => setShowImportAssetsDrawer(false)}
        processChunk={processCsvRows}
      />
    </Stack>
  );
};
export default TEPoliciesNewPage;
